import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
  crossfadeEnabled: boolean;
  onCrossfadeChange: (v: boolean) => void;
  melodyEnabled: boolean;
  onMelodyChange: (v: boolean) => void;
}

export default function SettingsModal({
  open,
  onClose,
  crossfadeEnabled,
  onCrossfadeChange,
  melodyEnabled,
  onMelodyChange,
}: SettingsModalProps) {
  const { t } = useTranslation();
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const titleId = 'settings-modal-title';

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    closeBtnRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      previouslyFocused?.focus();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="rounded-2xl p-6 w-full max-w-sm"
        style={{
          background: 'rgba(4,11,19,0.95)',
          border: '1px solid rgba(142,152,223,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 id={titleId} className="text-lg font-semibold text-white">
            {t('settings')}
          </h2>
          <button
            ref={closeBtnRef}
            onClick={onClose}
            aria-label={t('close')}
            className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <ToggleRow
          label={t('crossfade')}
          description={t('crossfadeDescription')}
          checked={crossfadeEnabled}
          onChange={onCrossfadeChange}
        />
        <div className="h-px my-3" style={{ background: 'rgba(142,152,223,0.15)' }} />
        <ToggleRow
          label={t('melody')}
          description={t('melodyDescription')}
          checked={melodyEnabled}
          onChange={onMelodyChange}
        />
      </div>
    </div>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="w-full flex items-center justify-between gap-4 py-2 text-left"
    >
      <div className="min-w-0">
        <p className="text-sm font-medium text-white">{label}</p>
        <p className="text-xs text-gray-400 mt-0.5">{description}</p>
      </div>
      <span
        aria-hidden="true"
        className="flex-shrink-0 w-10 h-6 rounded-full relative transition-colors"
        style={{ background: checked ? '#8E98DF' : 'rgba(255,255,255,0.1)' }}
      >
        <span
          className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all"
          style={{ left: checked ? 'calc(100% - 1.375rem)' : '0.125rem' }}
        />
      </span>
    </button>
  );
}
